// client/src/contexts/AccessContext.js
//
// WHICH PAGES MAY I OPEN — asked once, of the server, and shared.
//
// The phone has always asked: it calls /api/permissions/my-access and shows
// what comes back. The web decided locally instead, re-deriving privileges from
// /api/users/me and evaluating its own copy of PAGE_PERMISSIONS. Two
// implementations of one decision, and only one of them is the thing that
// actually enforces the request.
//
// THE MENU AND THE ROUTER MUST ASK THE SAME QUESTION. When only the menu
// consulted the server, a teacher saw "Class results" in the sidebar, clicked
// it, and `PermissionRoute` — still asking the local rules — sent them to the
// landing page. A visible item that throws you out is worse than no item, and
// that is precisely what one half of this fix produced without the other.
//
// A UNION, never a narrowing: a page opens if the server grants it OR the local
// rules do. A failed or slow request leaves `pages` null and everything behaves
// exactly as it did before this file existed.

import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { UserContext } from './UserContext';
import { fetchJson } from '../utils/fetchJson';

const AccessContext = createContext({ pages: null, grantedByServer: () => false });

export function AccessProvider({ children }) {
  const user = useContext(UserContext);
  const [pages, setPages] = useState(null);

  useEffect(() => {
    if (!user) { setPages(null); return; }
    let alive = true;
    (async () => {
      try {
        const data = await fetchJson('/api/permissions/my-access');
        const list = Array.isArray(data?.pages) ? data.pages : [];
        if (alive) setPages(new Set(list.map((p) => String(p))));
      } catch (_) {
        if (alive) setPages(null);
      }
    })();
    return () => { alive = false; };
  }, [user]);

  const value = useMemo(() => ({
    pages,
    // Exact match only. These are whole page paths, and a prefix match here
    // would quietly grant `/subjecthead/anything` off the back of one entry.
    grantedByServer: (path) => Boolean(pages && path && pages.has(String(path))),
  }), [pages]);

  return <AccessContext.Provider value={value}>{children}</AccessContext.Provider>;
}

export function useAccess() {
  return useContext(AccessContext) || { pages: null, grantedByServer: () => false };
}

export default AccessContext;
